<template>
  <v-dialog
    v-model="dialog"
    max-width="500px"
  >
    <v-card
      v-if="formLoading"
      class="d-flex justify-center"
    >
      <v-progress-circular
        :size="80"
        :width="5"
        color="primary"
        indeterminate
      />
    </v-card>

    <v-card v-else>
      <v-card-title>
        <span class="headline">{{ isEdite ? 'Update' : 'Add' }} Fee</span>
      </v-card-title>
      <v-card-text>
        <v-container>
          <v-row>
            <v-col
              cols="12"
              sm="6"
            >
              <v-autocomplete
                v-model="formData.sendCountryCode"
                :items="listSourceCountries"
                label="Send country"
                item-value="codeIso3"
                :item-text="i => `${i.codeIso3}: ${i.name}`"
                :disabled="isEdite"
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-autocomplete
                v-model="formData.sendCurrencyCode"
                :items="currencies"
                label="Sending Currency"
                :disabled="isEdite"
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-autocomplete
                v-model="formData.receiveCountryCode"
                :items="listDestCountries"
                item-value="codeIso3"
                :item-text="i => `${i.codeIso3}: ${i.name}`"
                label="Receive country"
                :disabled="isEdite"
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-autocomplete
                v-model="formData.receiveCurrencyCode"
                :items="currencies"
                label="Receiving Currency"
                :disabled="isEdite"
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-autocomplete
                v-model="formData.payOutMethod"
                :items="payoutMethods"
                label="Payout Methods"
                item-value="code"
                item-text="title"
                :disabled="isEdite"
              />
            </v-col>
            <v-col>
              <h4 class="text-h6">
                Fee Pricing Range
              </h4>
            </v-col>
            <v-row>
              <v-card
                v-for="(item, index) in feesRange"
                :key="`fees-range-item-${index}`"
                class="card-fee-range"
                outlined
                shaped
              >
                <v-card-text>
                  <v-row>
                    <v-btn
                      v-if="index > 0"
                      class="card-fee-range__remove"
                      icon
                      outlined
                      plain
                      fab
                      x-small
                      @click="onRemoveFeeRangeItem(index)"
                    >
                      <v-icon>mdi-close</v-icon>
                    </v-btn>
                    <v-col
                      cols="12"
                    >
                      <v-radio-group
                        v-model="item.type"
                        row
                      >
                        <v-radio
                          label="Fixed Fee"
                          value="fixed"
                        />
                        <v-radio
                          label="Percentage Fee"
                          value="percentage"
                        />
                      </v-radio-group>
                    </v-col>
                    <v-col
                      cols="12"
                      sm="6"
                    >
                      <v-text-field
                        v-model="item.minAmount"
                        type="number"
                        label="Min Amount value"
                      />
                    </v-col>
                    <v-col
                      cols="12"
                      sm="6"
                    >
                      <v-text-field
                        v-model="item.value"
                        type="number"
                        label="Value (Percentage fee rate)"
                        suffix="%"
                      />
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-row>
            <v-btn
              v-if="feesRange.length < feesRangeLimit"
              class="mt-2 primary"
              block
              @click="onAddFeeRange"
            >
              <v-icon
                class="mr-2"
              >
                mdi-plus
              </v-icon>
              Add fee range
            </v-btn>
          </v-row>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn @click="onClose">
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          :loading="loading"
          :disabled="loading"
          @click="onSubmit"
        >
          {{ isEdite ? 'Update' : 'Create' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
// utils
import { rateCurrencies } from "@/utils/staticAutopricing";

export default {
  name: "CreateFeesModal",

  props: {
    value: {
      type: Boolean,
      default: false
    },
    editedItem: {
      type: Object,
      default: () => null
    }
  },

  data: () => ({
    isEdite: false,
    formLoading: false,
    loading: false,
    currencies: rateCurrencies,
    formData: {
      sendCountryCode: '',
      receiveCountryCode: '',
      receiveCurrencyCode: '',
      payOutMethod: ''
    },
    feesRange: [
    ],
    feesRangeLimit: 50
  }),

  computed: {
    listDestCountries() {
      return this.$store.getters["ActiveCountryStore/getListDestCountries"];
    },
    listSourceCountries() {
      return this.$store.getters["ActiveCountryStore/getListSourceCountries"];
    },
    payoutMethods() {
      return this.$store.getters["PayoutMethodStore/getList"];
    },
    dialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      }
    },
    filterStringPayload() {
      const filter = this.editedItem;
      return `sendCountryCode:${filter.sendCountryCode},sendCurrencyCode:${filter.sendCurrencyCode},receiveCountryCode:${filter.receiveCountryCode},receiveCurrencyCode:${filter.receiveCurrencyCode},payOutMethod:${filter.payOutMethod}`;
    }
  },

  created() {
    if (this.editedItem) {
      this.isEdite = true;
      this.getPrefilledFees();
    } else {
      this.feesRange.push({
        type: 'fixed',
        minAmount: 0,
        value: 0
      });
    }
  },

  methods: {
    async getPrefilledFees () {
      this.formLoading = true;

      await this.$store
        .dispatch("FeesStore/getPrefilledFees", {
          page: 1,
          size: 50,
          filter: this.filterStringPayload
        })
        .then((res) => {
          const data = res.list
          this.onPrefillFormData(data);
        })
        .finally(() => this.formLoading = false);
    },

    onPrefillFormData (data) {
      if (data && data.length) {
        this.$set(this.formData, 'sendCountryCode', data[0].sendCountryCode);
        this.$set(this.formData, 'sendCurrencyCode', data[0].sendCurrencyCode);
        this.$set(this.formData, 'receiveCountryCode', data[0].receiveCountryCode);
        this.$set(this.formData, 'receiveCurrencyCode', data[0].receiveCurrencyCode);
        this.$set(this.formData, 'payOutMethod', data[0].payOutMethod);

        data.forEach((item) => {
          const fee = {
            type: item.type,
            minAmount: item.minAmount,
            value: item.value
          };

          this.feesRange.push(fee);
        });
      }
    },

    onRemoveFeeRangeItem (index) {
      this.$delete(this.feesRange, index);
    },

    onAddFeeRange () {
      this.feesRange.push({
        type: 'fixed',
        minAmount: 0,
        value: 0
      });
    },

    onSubmit () {
      this.loading = true;
      const data = {
        fees: []
      }

      const normalizedFormData = { ...this.formData };
      Object.keys(normalizedFormData).forEach(key => {
        if (normalizedFormData[key] === '') {
          normalizedFormData[key] = null;
        }
      });

      if (this.feesRange && this.feesRange.length) {
        this.feesRange.forEach((item) => {
          const fee = {
            ...normalizedFormData,
            ...item
          };

          data.fees.push(fee);
        });
      }

      this.$store
        .dispatch("FeesStore/createFees", data)
        .then(() => {
          this.$emit("on-create");
          this.onClose();
        })
        .finally(() => {
          this.loading = false;
        });
    },

    onClose () {
      this.dialog = false;
    }
  }
};
</script>

<style lang="scss">
.card-fee-range {
  position: relative;
  margin-bottom: 1rem;
  border-color: #ddd;

  &__remove {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    z-index: 2;
  }

  .v-input {
    margin: 0;
  }

  .v-input__slot {
    margin-bottom: 0;
  }
}

.v-progress-circular {
  margin: 3rem auto;
}
</style>